// Non-visual confirmation for the floor (spec 06 §4 floor rows).
//
// Every Zebra and Honeywell handheld a KazKabel storekeeper has ever used beeps
// on a good scan and buzzes differently on a bad one, and that is not decoration:
// in a noisy plant, with a glove on, phone at waist height, the operator does not
// look at the screen for each of twenty bobbins. Without it a good scan, a bad
// scan and a scanner that never fired are all indistinguishable — so the operator
// stops trusting the app and starts re-scanning everything.
//
// Deliberately tiny and deliberately best-effort:
//  · `navigator.vibrate` is absent on iOS Safari and on desktop; the calls are
//    guarded and silent rather than feature-detected in the UI, because a
//    missing buzz must never change what the screen says;
//  · no audio file is bundled — a short WebAudio tone pair costs nothing to ship
//    and cannot fail to load;
//  · audio is OFF unless the device opts in (a plant may forbid it, and an
//    unexpected beep in a quiet office is worse than none), while haptics are on
//    by default because a phone in a pocket cannot annoy anyone.

const AUDIO_KEY = "assessly.floor.sound";

export type FeedbackKind = "ok" | "warn" | "error";

function safeStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

export function soundEnabled(): boolean {
  try {
    return safeStorage()?.getItem(AUDIO_KEY) === "on";
  } catch {
    return false;
  }
}

export function setSoundEnabled(on: boolean): void {
  try {
    safeStorage()?.setItem(AUDIO_KEY, on ? "on" : "off");
  } catch {
    /* non-fatal */
  }
}

const PATTERNS: Record<FeedbackKind, number | number[]> = {
  // One short pulse: "that landed". Long enough to feel through a glove.
  ok: 40,
  // Two pulses: "saved, but read the screen" (queued offline, variance needed).
  warn: [40, 60, 40],
  // Three, longer: "this did NOT go through".
  error: [80, 60, 80, 60, 80],
};

const TONES: Record<FeedbackKind, { freq: number; ms: number }> = {
  ok: { freq: 1760, ms: 70 },
  warn: { freq: 880, ms: 110 },
  error: { freq: 320, ms: 180 },
};

type AudioCtor = new () => AudioContext;

function audioCtor(): AudioCtor | undefined {
  const scope = globalThis as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor };
  return scope.AudioContext ?? scope.webkitAudioContext;
}

let context: AudioContext | undefined;

function beep(kind: FeedbackKind): void {
  const Ctor = audioCtor();
  if (Ctor === undefined) return;
  try {
    context ??= new Ctor();
    const tone = TONES[kind];
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = tone.freq;
    gain.gain.value = 0.08;
    oscillator.connect(gain).connect(context.destination);
    const now = context.currentTime;
    oscillator.start(now);
    oscillator.stop(now + tone.ms / 1000);
  } catch {
    // A browser that refuses to make noise is not a reason to fail a scan.
  }
}

/** Buzz (and optionally beep). Safe to call from any event handler. */
export function floorFeedback(kind: FeedbackKind): void {
  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(PATTERNS[kind]);
    }
  } catch {
    /* non-fatal */
  }
  if (soundEnabled()) beep(kind);
}
